import type { Editor } from '@tiptap/core';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { getSocialNetworkMeta } from './blockLibrary';
import { BlockToolbar } from '../components/BlockToolbar';
import { EmailBlockTipTap } from '../components/EmailBlockTipTap';
import { InlineFormatBar } from '../components/InlineFormatBar';
import type { BlockPatch, EmailBlock } from '../HtmlEditor.types';

type BlockViewProps = {
  block: EmailBlock;
  selected: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onTextEdit: (text: string) => void;
  onChange?: (patch: BlockPatch) => void;
  disabled?: boolean;
};

export function BlockView({
  block: b,
  selected,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onTextEdit,
  onChange,
  disabled,
}: BlockViewProps) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const isRichText = b.type === 'text' || b.type === 'title';
  const showInlineBar = selected && !disabled && isRichText;

  const handleEditorReady = (nextEditor: Editor | null) => {
    setEditor(nextEditor);
  };

  const wrapStyle: CSSProperties = {
    padding: `${b.paddingTop}px ${b.paddingRight}px ${b.paddingBottom}px ${b.paddingLeft}px`,
    margin: `${b.marginTop}px ${b.marginRight}px ${b.marginBottom}px ${b.marginLeft}px`,
    background: b.bg !== 'transparent' ? b.bg : undefined,
    backgroundImage: b.bgImage ? `url(${b.bgImage})` : undefined,
    backgroundSize: 'cover',
    textAlign: b.align,
    position: 'relative',
  };

  const wrapper = (children: ReactNode) => (
    <div
      className={`ee-bview ${selected ? 'is-selected' : ''} ${showInlineBar ? 'has-inlinebar' : ''}`}
      style={wrapStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onSelect();
      }}
    >
      {selected && !disabled && (
        <BlockToolbar
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      )}
      {children}
      {showInlineBar && (
        <InlineFormatBar block={b} editor={editor} onChange={onChange} />
      )}
    </div>
  );

  if (b.type === 'title') {
    const richTextStyle: CSSProperties = {
      margin: 0,
      fontSize: b.size,
      color: b.color,
      fontFamily: b.font,
      fontWeight: b.weight,
      lineHeight: b.lineHeight,
    };

    return wrapper(
      <EmailBlockTipTap
        blockId={b.id}
        content={b.text}
        blockType='title'
        disabled={disabled}
        style={richTextStyle}
        onChange={onTextEdit}
        onEditorReady={handleEditorReady}
      />
    );
  }

  if (b.type === 'text') {
    const richTextStyle: CSSProperties = {
      margin: 0,
      fontSize: b.size,
      color: b.color,
      fontFamily: b.font,
      fontWeight: b.weight,
      lineHeight: b.lineHeight,
    };

    return wrapper(
      <EmailBlockTipTap
        blockId={b.id}
        content={b.text}
        blockType='text'
        disabled={disabled}
        style={richTextStyle}
        onChange={onTextEdit}
        onEditorReady={handleEditorReady}
      />
    );
  }

  if (b.type === 'image') {
    if (b.src) {
      return wrapper(
        <img src={b.src} alt='' style={{ maxWidth: '100%', height: b.height, display: 'block', margin: '0 auto' }} />
      );
    }
    return wrapper(
      <div
        style={{
          background: 'linear-gradient(135deg, #FBBF24 0%, #F472B6 50%, #A78BFA 100%)',
          height: b.height,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 4,
          color: 'white',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 56, lineHeight: 1 }}>{b.placeholder || '🌄'}</div>
          <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>Click to upload an image</div>
        </div>
      </div>
    );
  }

  if (b.type === 'video') {
    return wrapper(
      <div
        style={{
          background: '#0F172A',
          height: b.height,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 4,
          color: 'white',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>▶</div>
          <div style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>Video thumbnail</div>
        </div>
      </div>
    );
  }

  if (b.type === 'button') {
    return wrapper(
      <a
        href={b.href}
        onClick={(e) => e.preventDefault()}
        style={{
          display: 'inline-block',
          background: b.btnBg,
          color: b.btnColor,
          padding: `${b.paddingTop || 14}px 24px`,
          borderRadius: b.radius,
          fontWeight: 700,
          textDecoration: 'none',
          fontSize: b.size,
        }}
      >
        {b.text}
      </a>
    );
  }

  if (b.type === 'divider') {
    return wrapper(<div style={{ height: b.thickness, background: b.color }} />);
  }

  if (b.type === 'spacer') {
    return wrapper(
      <div
        style={{
          height: b.height,
          border: selected ? '1px dashed var(--postel-blue)' : '1px dashed transparent',
        }}
      />
    );
  }

  if (b.type === 'logo') {
    return wrapper(
      <div style={{ fontFamily: b.font, fontWeight: b.weight, fontSize: b.size, color: b.color }}>{b.text}</div>
    );
  }

  if (b.type === 'social') {
    if (b.links.length === 0) {
      return wrapper(
        <div style={{ fontSize: 12, color: 'var(--ink-500)', fontStyle: 'italic' }}>
          Add social networks in the inspector
        </div>
      );
    }

    return wrapper(
      <div style={{ display: 'inline-flex', gap: 8 }}>
        {b.links.map((link) => {
          const glyph = getSocialNetworkMeta(link.network)?.glyph ?? '◎';
          return (
            <a
              key={link.network}
              href={link.url || '#'}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.preventDefault()}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#0F172A',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              {glyph}
            </a>
          );
        })}
      </div>
    );
  }

  if (b.type === 'product') {
    return wrapper(
      <div style={{ display: 'inline-block', textAlign: 'center', padding: 12 }}>
        <div style={{ fontSize: 56 }}>{b.img}</div>
        <div style={{ fontWeight: 700, marginTop: 6 }}>{b.name}</div>
        <div style={{ color: 'var(--postel-blue)', fontWeight: 700 }}>{b.price}</div>
      </div>
    );
  }

  if (b.type === 'nav') {
    return wrapper(
      <div style={{ display: 'inline-flex', gap: 16 }}>
        {b.items.map((it) => (
          <a
            key={it}
            href='#'
            onClick={(e) => e.preventDefault()}
            style={{ color: b.color, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
          >
            {it}
          </a>
        ))}
      </div>
    );
  }

  if (b.type === 'dynamic') {
    return wrapper(
      <span
        style={{
          background: 'var(--postel-blue-light)',
          color: 'var(--postel-blue)',
          padding: '4px 8px',
          borderRadius: 4,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {`{{ ${b.source} | ${b.fallback} }}`}
      </span>
    );
  }

  if (b.type === 'html') {
    return wrapper(
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-500)',
          fontFamily: 'JetBrains Mono, monospace',
          background: 'var(--ink-50)',
          padding: 12,
          borderRadius: 4,
        }}
      >
        {b.code}
      </div>
    );
  }

  return wrapper(<div>Unknown block</div>);
}
